/**
 * The composer as a drop target for workspace items (`droppedContexts`).
 *
 * Returned handlers go through the composer's `getRootProps(...)`, beside
 * react-dropzone's own. The two never see each other's drags: these act only
 * on the item drag's private type, react-dropzone only on OS `Files`. Like
 * the file route, not gated mid-stream: adding context sends nothing.
 */

import type React from 'react';
import { useState } from 'react';

import type { ChatContext } from '../../../contexts/ChatContextProvider';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import type { useToast } from '../../../hooks/ui/useToast';
import { safeAnalytics } from '../../../utils/analytics';
import {
  isItemDrag,
  readItemDragPayload,
  type DraggedItem,
} from '../../navigation/HierarchyTree.dnd';
import { mergeDroppedContexts, type DroppedContextsResult } from './droppedContexts';

type Toast = ReturnType<typeof useToast>;

/** The one toast a drop deserves, or none when every item was added. */
function dropFeedback(result: DroppedContextsResult, max: number): Parameters<Toast>[0] | null {
  if (result.unreadable > 0) {
    // Counts the cap's refusals too, so a mixed drop never undercounts.
    const refused = result.unreadable + result.overCap;
    const noun = refused === 1 ? 'item' : 'items';
    const overCap = result.overCap > 0 ? ` ${result.overCap} went past the ${max}-item limit.` : '';
    return {
      title: `${refused} ${noun} couldn't be added`,
      description: `Reload the tab you dragged them from, then try again.${overCap}`,
      status: 'warning',
    };
  }
  if (result.overCap > 0) {
    return {
      title: `You can attach up to ${max} items`,
      description: `${result.overCap} not added. Remove one to add another.`,
      status: 'info',
    };
  }
  if (result.added === 0) return { title: 'Already in context', status: 'info' };
  return null;
}

export function useComposerItemDrop({
  manual,
  shown,
  max,
  setManual,
  toast,
}: {
  /** The researcher's own picks. */
  readonly manual: readonly ChatContext[];
  /** Every chip, the page's auto context included. */
  readonly shown: readonly ChatContext[];
  readonly max: number;
  readonly setManual: (next: readonly ChatContext[]) => void;
  readonly toast: Toast;
}) {
  const [isItemDragOver, setIsItemDragOver] = useState(false);

  // Reads the rendered picks rather than deciding inside a state updater, so
  // the toast cannot fire twice (same reasoning as the composer's
  // `stageAccepted`).
  const handleDrop = (dropped: readonly DraggedItem[]) => {
    if (dropped.length === 0) {
      toast({ title: "Couldn't add these items", status: 'warning' });
      return;
    }
    const result = mergeDroppedContexts(manual, shown, dropped, max);
    if (result.added > 0) setManual(result.next);
    const folderCount = dropped.filter((item) => item.kind === 'folder').length;
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_CONTEXT_DROPPED, {
      item_count: dropped.length,
      added_count: result.added,
      folder_count: folderCount,
      file_count: dropped.length - folderCount,
    });
    const feedback = dropFeedback(result, max);
    if (feedback) toast(feedback);
  };

  const handlers = {
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      if (!isItemDrag(e.dataTransfer)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (!isItemDragOver) setIsItemDragOver(true);
    },
    onDragLeave: (e: React.DragEvent<HTMLElement>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setIsItemDragOver(false);
    },
    onDrop: (e: React.DragEvent<HTMLElement>) => {
      setIsItemDragOver(false);
      if (!isItemDrag(e.dataTransfer)) return;
      e.preventDefault();
      handleDrop(readItemDragPayload(e.dataTransfer));
    },
  };

  return { isItemDragOver, handlers };
}
